import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import DataTable from "react-data-table-component";
import { Container, Input } from "reactstrap";
import { toast } from "react-toastify";
import { appPath } from "../../../utils/appPaths";
import { Breadcrumbs } from "../../../AbstractElements";
import ContributorService from "../../../Services/contributors/ContributorService";
import ProductService from "../../../Services/products/ProductService";
import SpinnerLoader from "../../Loaders/Spinner";
import ContributorEditModal from "./ContributorEditModal";
import ContributorRowActions from "./ContributorRowActions";
import {
  buildSplitStatsByContributor,
  ContributorBankBadge,
  ContributorRoleBadge,
  ContributorSplitBadge,
  getContributorRoles,
  hasCompleteBankInfo,
} from "./ContributorListHelpers";
import "./contributors-list.scss";
import { getApiErrorMessage } from "../../../utils/apiErrorMessage";

function ContributorsContainer() {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [splitsFilter, setSplitsFilter] = useState("all");
  const [bankFilter, setBankFilter] = useState("all");
  const [selectedContributor, setSelectedContributor] = useState(null);
  const [errors, setErrors] = useState({});

  const { isPending, isError, data, error } = useQuery({
    queryKey: ["contributors"],
    queryFn: ContributorService.getAllContributors,
  });

  const { data: productsData } = useQuery({
    queryKey: ["products"],
    queryFn: ProductService.getAllProducts,
  });

  const contributors = data?.data ?? [];

  const splitStatsByContributor = useMemo(
    () => buildSplitStatsByContributor(productsData),
    [productsData]
  );

  const allRoles = useMemo(() => {
    const set = new Set();
    contributors.forEach((item) => {
      getContributorRoles(item).forEach((role) => set.add(role));
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [contributors]);

  const summary = useMemo(() => {
    const withBank = contributors.filter(hasCompleteBankInfo).length;
    const withSplits = contributors.filter(
      (item) => (splitStatsByContributor[Number(item.id)]?.splitCount || 0) > 0
    ).length;

    return {
      total: contributors.length,
      withBank,
      withSplits,
      uniqueRoles: allRoles.length,
    };
  }, [contributors, splitStatsByContributor, allRoles.length]);

  const filteredContributors = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    return contributors.filter((contributor) => {
      const roles = getContributorRoles(contributor);
      const rolesText = roles.join(" ").toLowerCase();
      const stats = splitStatsByContributor[Number(contributor.id)];
      const splitCount = stats?.splitCount || 0;
      const bankComplete = hasCompleteBankInfo(contributor);

      const matchesSearch =
        !term ||
        [
          contributor.contributor_name,
          rolesText,
          contributor.currency,
          contributor.ca_ipi,
          contributor.bank_name,
        ]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(term));

      const matchesRole =
        roleFilter === "all" ||
        roles.some((role) => role.toLowerCase() === roleFilter.toLowerCase());

      const matchesSplits =
        splitsFilter === "all" ||
        (splitsFilter === "with" && splitCount > 0) ||
        (splitsFilter === "without" && splitCount === 0);

      const matchesBank =
        bankFilter === "all" ||
        (bankFilter === "complete" && bankComplete) ||
        (bankFilter === "incomplete" && !bankComplete);

      return matchesSearch && matchesRole && matchesSplits && matchesBank;
    });
  }, [contributors, searchTerm, roleFilter, splitsFilter, bankFilter, splitStatsByContributor]);

  const updateMutation = useMutation({
    mutationFn: ContributorService.updateContributor,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contributors"] });
      toast.success("Contributor updated successfully");
      setSelectedContributor(null);
      setErrors({});
    },
    onError: (mutationError) => {
      if (mutationError.response?.data?.errors) {
        setErrors(mutationError.response.data.errors);
      } else {
        toast.error(
          `Failed to update contributor: ${getApiErrorMessage(
            mutationError,
            "Update failed"
          )}`
        );
      }
    },
  });

  const handleEdit = (contributor) => {
    setSelectedContributor(contributor);
    setErrors({});
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!selectedContributor) return;

    const formData = {
      contributor_name: selectedContributor.contributor_name,
      contributor_role: selectedContributor.contributor_role,
      bank_account_number: event.target.bank_account_number.value,
      bank_iban_number: event.target.bank_iban_number.value,
      bank_swift_number: event.target.bank_swift_number.value,
      bank_name: event.target.bank_name.value,
      ca_ipi: event.target.ca_ipi.value,
      currency: event.target.currency.value,
    };

    updateMutation.mutate({ id: selectedContributor.id, formData });
  };

  const tableColumns = useMemo(
    () => [
      {
        name: "Contributor",
        selector: (row) => row.contributor_name,
        sortable: true,
        minWidth: "140px",
        grow: 1.4,
        cell: (row) => (
          <div className="ctr-name-cell">
            <Link
              to={appPath(`/contributors/${row.id}`)}
              className="ctr-name-cell__link"
              title={row.contributor_name}
            >
              {row.contributor_name}
            </Link>
            <div className="ctr-name-cell__meta">
              {[row.currency, row.ca_ipi ? `IPI ${row.ca_ipi}` : null]
                .filter(Boolean)
                .join(" · ") || "—"}
            </div>
          </div>
        ),
      },
      {
        name: "Roles",
        sortable: true,
        minWidth: "140px",
        grow: 1.6,
        cell: (row) => {
          const roles = getContributorRoles(row);
          if (!roles.length) return <span className="text-muted">—</span>;
          return (
            <div className="ctr-roles">
              {roles.map((role, index) => (
                <ContributorRoleBadge key={`${role}-${index}`} role={role} />
              ))}
            </div>
          );
        },
      },
      {
        name: "Bank",
        sortable: true,
        width: "140px",
        minWidth: "140px",
        selector: (row) => hasCompleteBankInfo(row),
        cell: (row) => <ContributorBankBadge complete={hasCompleteBankInfo(row)} />,
      },
      {
        name: "Splits",
        sortable: true,
        width: "150px",
        minWidth: "150px",
        sortFunction: (a, b) => {
          const aCount = splitStatsByContributor[Number(a.id)]?.splitCount || 0;
          const bCount = splitStatsByContributor[Number(b.id)]?.splitCount || 0;
          return aCount - bCount;
        },
        cell: (row) => {
          const stats = splitStatsByContributor[Number(row.id)];
          return (
            <ContributorSplitBadge
              splitCount={stats?.splitCount || 0}
              assetCount={stats?.assetIds?.size || 0}
            />
          );
        },
      },
      {
        name: "Actions",
        width: "120px",
        minWidth: "120px",
        center: true,
        cell: (row) => (
          <ContributorRowActions contributor={row} onEdit={handleEdit} />
        ),
      },
    ],
    [splitStatsByContributor]
  );

  if (isError) {
    return (
      <div className="alert alert-danger m-3">
        Failed to load contributors: {error?.message || "Unknown error"}
      </div>
    );
  }

  return (
    <>
      <Breadcrumbs parent="Contributors" title="Contributors" mainTitle="Contributors" />

      <div className="rd-contributors-list">
        <Container fluid>
          <div className="ctr-stats">
            <div className="ctr-stat">
              <p className="ctr-stat__label">Total</p>
              <p className="ctr-stat__value">{summary.total}</p>
            </div>
            <div className="ctr-stat">
              <p className="ctr-stat__label">Bank complete</p>
              <p className="ctr-stat__value">{summary.withBank}</p>
            </div>
            <div className="ctr-stat">
              <p className="ctr-stat__label">With splits</p>
              <p className="ctr-stat__value">{summary.withSplits}</p>
            </div>
            <div className="ctr-stat">
              <p className="ctr-stat__label">Roles</p>
              <p className="ctr-stat__value">{summary.uniqueRoles}</p>
            </div>
          </div>

          <div className="ctr-toolbar">
            <div className="ctr-toolbar__search">
              <Input
                type="search"
                placeholder="Search name, role, bank, currency, IPI…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <Input
              type="select"
              className="ctr-toolbar__filter"
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
            >
              <option value="all">All roles</option>
              {allRoles.map((role) => (
                <option key={role} value={role}>
                  {role}
                </option>
              ))}
            </Input>
            <Input
              type="select"
              className="ctr-toolbar__filter"
              value={splitsFilter}
              onChange={(e) => setSplitsFilter(e.target.value)}
            >
              <option value="all">All splits</option>
              <option value="with">Has splits</option>
              <option value="without">No splits</option>
            </Input>
            <Input
              type="select"
              className="ctr-toolbar__filter"
              value={bankFilter}
              onChange={(e) => setBankFilter(e.target.value)}
            >
              <option value="all">All bank</option>
              <option value="complete">Bank complete</option>
              <option value="incomplete">Bank incomplete</option>
            </Input>
          </div>

          <div className="ctr-panel">
            {isPending ? (
              <SpinnerLoader />
            ) : filteredContributors.length === 0 ? (
              <div className="ctr-empty">No contributors match your filters.</div>
            ) : (
              <div className="ctr-table-wrap">
                <DataTable
                  columns={tableColumns}
                  data={filteredContributors}
                  pagination
                  paginationPerPage={25}
                  paginationRowsPerPageOptions={[10, 25, 50, 100]}
                  highlightOnHover
                  responsive
                  noDataComponent="No contributors found."
                />
              </div>
            )}
          </div>
        </Container>
      </div>

      <ContributorEditModal
        isOpen={Boolean(selectedContributor)}
        toggle={() => setSelectedContributor(null)}
        contributor={selectedContributor}
        errors={errors}
        isSaving={updateMutation.isPending}
        onSubmit={handleSubmit}
      />
    </>
  );
}

export default ContributorsContainer;
